import CategoryLayout from "@app/layouts/Category/CategoryLayout";
import ErrorLayout from "@app/layouts/ErrorLayout/ErrorLayout";
import { SectionData, SectionPageData } from "@app/types/ServerCustomContext";
import AdSettings from "@app/utils/AdSettings";
import { extractUrlRedirect } from "@app/utils/extractUrlRedirect";
import {
  GA4_REF_CODE_ANCHORBLOCK_B,
  GA4_REF_CODE_CONTENTBLOCK_L,
  GA4_REF_CODE_TITLE_STACKED,
} from "@app/utils/ga/ref-code";
import {
  buildUrlWithGARefCode,
  getContentBlockRefCode,
} from "@app/utils/ga/tracking";
import {
  createTeaserMapper,
  mapTeaserDataToAnchor,
  mapTeaserDataToLatestProps,
} from "@app/utils/mapTeaserDataToProps";
import MetaData from "@components/Modules/MetaData";
import {
  mapErrorDataToMetaData,
  mapSectionDataToMetaData,
} from "@components/Modules/MetaData/mappers";
import PubBaseLayout from "@pub/BaseLayout";
import { configCategory, configTrackingCode } from "@pub/config";
import { Route } from "@route-types/Category/+types/Category";
import { GlobalBaseData, SectionContext } from "@sphtech/dmg-design-system";
import { ResponseType, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import { extractGlobalData } from "@src/app/utils/globalData";
import { useLoaderData, useLocation, useRouteLoaderData } from "react-router";

import { fetchCategoryData } from "./Category.server";

export const meta = ({
  loaderData,
  location,
}: {
  loaderData: TRouteWithRedirect<SectionPageData & GlobalBaseData, string>;
  location: { pathname: string };
}) => {
  if (loaderData.type !== ResponseType.SUCCESS) {
    const errorData = mapErrorDataToMetaData(loaderData, location);
    return MetaData(errorData);
  }

  const sectionData = loaderData.payload;
  const metaData = mapSectionDataToMetaData(sectionData);
  return MetaData(metaData);
};

export default function Category() {
  const response: TRouteWithRedirect<SectionPageData & GlobalBaseData, string> =
    useLoaderData();
  const rootData: GlobalBaseData | undefined = useRouteLoaderData("root");
  // Context from server.ts
  const location = useLocation();

  if (response.type !== ResponseType.SUCCESS) {
    return <ErrorLayout response={response} />;
  }

  const sectionData = response.payload;
  const topStory = sectionData.topStories[0];
  const userSubmissionBox =
    rootData &&
    rootData.userSubmissionBox?.showOn.sectionPaths?.includes(sectionData.path)
      ? rootData.userSubmissionBox
      : undefined;

  const metaData = mapSectionDataToMetaData(sectionData);
  return (
    <SectionContext.Provider
      value={{ uniqueName: sectionData.uniqSectionName }}
    >
      <PubBaseLayout>
        <AdSettings
          pageType="category"
          sectionName={metaData.title}
          adUnits="lb1,lb2,imu1,imu2,catfish,prestitial"
          pageTitle={metaData.title}
          currentLocation={location}
        />

        <CategoryLayout
          variant={configCategory.layoutVariant}
          data={{
            uniqSectionName: sectionData.uniqSectionName,
            name: sectionData.name,
            nameZH: sectionData.nameZH,
            subHeading: sectionData.parent,
            anchorCarousel: sectionData.topStories.map((story) =>
              mapTeaserDataToAnchor(story, GA4_REF_CODE_ANCHORBLOCK_B),
            ),
            anchorBlock: topStory
              ? mapTeaserDataToAnchor(topStory, GA4_REF_CODE_ANCHORBLOCK_B)
              : undefined,
            latestArticle: sectionData.topStories
              .slice(1)
              .map((teaser) =>
                mapTeaserDataToLatestProps(
                  teaser,
                  configTrackingCode.category?.latestArticle ||
                    GA4_REF_CODE_TITLE_STACKED,
                ),
              ),
            mainContentBlock: sectionData.listedStories.map((teaser) =>
              mapTeaserDataToLatestProps(
                teaser,
                configTrackingCode.category?.mainContentBlock ||
                  GA4_REF_CODE_CONTENTBLOCK_L,
              ),
            ),
            listGroup: sectionData.listGroup && {
              ...sectionData.listGroup,
              items: sectionData.listGroup.items.map((item) => ({
                ...item,
                url: buildUrlWithGARefCode(
                  item.url,
                  configTrackingCode.category?.listGroup,
                ),
              })),
            },
            videoGallery: {
              title: sectionData.videoGallery?.title || "",
              teasers: sectionData.videoGallery?.teasers
                ? sectionData.videoGallery.teasers.map(
                    createTeaserMapper({
                      gaRefCode: getContentBlockRefCode("videoplaylist"),
                    }),
                  )
                : [],
            },
            showVerticalLine: configCategory.showVerticalLine,
            userSubmissionBox: userSubmissionBox,
            storyType: sectionData.storyType,
            path: sectionData.path,
          }}
        />
      </PubBaseLayout>
    </SectionContext.Provider>
  );
}

export async function loader({
  params,
  request,
}: Route.LoaderArgs): Promise<
  TRouteWithRedirect<SectionData | GlobalBaseData, GlobalBaseData | string>
> {
  const section = params.section;

  // Validate section parameter - reject common invalid values
  const invalidSections = ["undefined", "null", ""];
  if (invalidSections.includes(section)) {
    const globalData = await extractGlobalData();

    return {
      type: ResponseType.CLIENT_ERROR,
      statusCode: 404,
      payload: globalData,
    };
  }

  const data = await fetchCategoryData(section);

  if (data.statusCode === 404) {
    const redirectResult = await extractUrlRedirect(request.url);

    if (redirectResult && redirectResult.target) {
      return redirectResult;
    }
  }

  return await dataLoader({
    type: data.type,
    dataSource: data,
  });
}
